import Logo from "@/assets/icons/logo.svg?react";
import { Table } from "@/components";
import { useUIStore } from "@/stores";
import {
  clipboard,
  formatDate,
  formatName,
  formatUserRoleToName,
  formatUserStatus,
  ImgSrc,
  MARKETING_SITE_URL_DOWNLOAD,
  LocalStorageService,
  ROUTE,
} from "@/utils";
import {
  Avatar,
  Box,
  Button,
  Grid,
  Link,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";
import { FC, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useGetAcquiredUsers, useGetMe, useGetPayouts } from "../../hooks";
import { UpdateBankCredentialsModal } from "../../modals";
import { DashboardPageProps } from "./dashboard-page.types";

const PER_PAGE = 5;

const DashboardPage: FC<DashboardPageProps> = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [page, setPage] = useState(0);

  const { me, isMeError } = useGetMe();
  const { payouts } = useGetPayouts();
  const { acquiredUsers, meta } = useGetAcquiredUsers(page, PER_PAGE);
  const { openModal } = useUIStore();

  useEffect(() => {
    if (isMeError) {
      toast.error("Something went wrong");
    }
  }, [isMeError]);

  if (!me) {
    return;
  }

  return (
    <Box sx={{ bgcolor: "#000", color: "#fff", p: 4, minHeight: "100vh" }}>
      <Stack direction={"row"} justifyContent={"space-between"}>
        <Logo />

        <Stack direction={"row"} spacing={1}>
          <Button
            variant="contained"
            color="secondary"
            onClick={() =>
              clipboard(`${MARKETING_SITE_URL_DOWNLOAD}?referralCode=${me.code}`)
                .then(() => toast.success("Copied!"))
                .catch((err) => toast.error(err))
            }
          >
            Copy Link For Referrals
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              LocalStorageService.deleteAuthTokens();
              queryClient.clear();
              queryClient.cancelQueries();
              navigate(ROUTE.AUTH_LOGIN, { replace: true });
            }}
          >
            Logout
          </Button>
        </Stack>
      </Stack>

      <Stack
        direction={"row"}
        justifyContent={"space-between"}
        alignItems={"center"}
      >
        <Typography variant="h5" gutterBottom>
          {me.fullName}
        </Typography>
        <Typography
          variant="body2"
          sx={{ cursor: "pointer" }}
          onClick={() =>
            clipboard(me.id)
              .then(() => toast.success("User ID copied!"))
              .catch((error) => {
                console.error(error.message);
                toast.error(error.message);
              })
          }
        >
          ID: {me.id}
        </Typography>
      </Stack>

      <Grid container spacing={2} sx={{ mt: 2 }}>
        <Grid item xs={12} sm={6}>
          <Typography>Email Address</Typography>
          <Typography>{me.email}</Typography>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Typography>Phone Number</Typography>
          <Typography>{me.phoneNumber}</Typography>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Typography>Web Or Social</Typography>
          {me.socialUrl ? (
            <Link href={me.socialUrl} color="primary" target="_blank">
              Link
            </Link>
          ) : (
            <Typography>—</Typography>
          )}
        </Grid>
        <Grid item xs={12} sm={6}>
          <Typography>Earning</Typography>
          <Typography>${me.earnings / 100}</Typography>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Typography>Muse Referral Hits</Typography>
          <Typography>{me.referralsCounts?.muse ?? 0}</Typography>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Typography>Alpha Referral Hits</Typography>
          <Typography>{me.referralsCounts?.alpha ?? 0}</Typography>
        </Grid>
      </Grid>

      <Box sx={{ mt: 4 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Muse Referral Payment"
              value={`$ ${(me.museReferralPaymentCents / 100).toFixed(2)}`}
              InputProps={{ readOnly: true }}
              variant="filled"
              sx={{ input: { color: "#fff" }, label: { color: "#ccc" } }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Alpha Referral Payment"
              value={`% ${me.alphaReferralPaymentBps / 100}`}
              InputProps={{ readOnly: true }}
              variant="filled"
              sx={{ input: { color: "#fff" }, label: { color: "#ccc" } }}
            />
          </Grid>
        </Grid>

        <Box sx={{ mt: 4 }}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            width={"100%"}
          >
            <Typography gutterBottom>Bank account credentials</Typography>
            <Button
              onClick={() =>
                openModal(UpdateBankCredentialsModal, {
                  title: "Update Bank Credentials",
                })
              }
            >
              Update
            </Button>
          </Stack>
          <Paper
            variant="outlined"
            sx={{ p: 2, bgcolor: "#222", color: "#ccc" }}
          >
            <Typography whiteSpace="pre-line">
              {me.bankAccountCredentials}
            </Typography>
          </Paper>
        </Box>

        <Box sx={{ mt: 4 }}>
          <Typography variant="h6" gutterBottom>
            Payouts
          </Typography>
          {payouts?.length ? (
            <Table
              headCells={["Period", "Date of Payment", "Amount"]}
              bodyCells={payouts.map(({ month, year, paidAt, amountCents }) => [
                dayjs(`${month.toString().padStart(2, "0")}-01-${year}`).format(
                  "MMMM YYYY"
                ),
                formatDate(paidAt ?? ""),
                amountCents / 100,
              ])}
            />
          ) : (
            <Typography>No data</Typography>
          )}
        </Box>

        <Box sx={{ mt: 4 }}>
          <Typography variant="h6" gutterBottom>
            Acquired Users
          </Typography>
          {acquiredUsers && meta && meta.total > 0 ? (
            <Table
              page={page}
              rowsPerPage={PER_PAGE}
              rowsPerPageOptions={[]}
              headCells={["User", "Role", "Status"]}
              count={meta.total}
              onPageChange={setPage}
              bodyCells={acquiredUsers.map((cell) => {
                const { id, firstName, lastName, role, status, profileImage } =
                  cell;
                return [
                  <Stack
                    direction={"row"}
                    spacing={2}
                    alignItems={"center"}
                    key={id}
                  >
                    <Avatar
                      alt="avatar"
                      src={ImgSrc(profileImage?.path ?? "")}
                      sx={{ height: 32, width: 32 }}
                    >
                      {!profileImage?.path &&
                        (firstName?.[0] || lastName?.[0] || "?")}
                    </Avatar>
                    <Typography component="span" variant="body2">
                      {formatName(firstName ?? "", lastName ?? "")}
                    </Typography>
                  </Stack>,
                  formatUserRoleToName(role),
                  formatUserStatus(status),
                ];
              })}
            />
          ) : (
            <Typography>No data</Typography>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export { DashboardPage };
